import { FC, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, FlaskConical } from 'lucide-react';
import { Button } from 'components/ui/Button';
import { funnelExpressionsAPI } from 'services/swagger-api';
import { TestExpressionResponse } from 'api/funnel';
import type { ExpressionVariable } from './ExpressionBuilder';
import { Token, canonicalizeExpression } from './expressionTokens';

interface ExpressionTesterProps {
  /** Formule telle que saisie (le back la met en forme canonique). */
  expression: string;
  tokens: Token[];
  variables: ExpressionVariable[];
  disabled?: boolean;
}

/**
 * Test à blanc de la formule : une valeur d'essai par variable référencée, puis le back
 * calcule et renvoie le résultat ou l'erreur exacte. Rien n'est enregistré. C'est le
 * seul contrôle complet : le builder ne connaît pas la grammaire du moteur, le back si.
 */
export const ExpressionTester: FC<ExpressionTesterProps> = ({
  expression,
  tokens,
  variables,
  disabled,
}) => {
  const codes = useMemo(() => {
    const seen = new Set<string>();
    tokens.forEach((token) => {
      if (token.kind === 'var' && token.code) seen.add(token.code);
    });
    return Array.from(seen);
  }, [tokens]);

  const [values, setValues] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<TestExpressionResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  // Optionnel : replié tant qu'on ne demande pas un test.
  const [isOpen, setIsOpen] = useState(false);

  // Un résultat affiché ne vaut que pour la formule qui l'a produit ; la mise en forme
  // (espaces, retours à la ligne) ne change pas la formule.
  const canonical = canonicalizeExpression(expression);
  useEffect(() => {
    setOutcome(null);
    setFailure(null);
  }, [canonical]);

  const variableOf = (code: string) => variables.find((v) => v.code === code);

  const run = async () => {
    setIsRunning(true);
    setFailure(null);
    try {
      const response = await funnelExpressionsAPI.testExpression({
        testExpressionCommand: {
          expression,
          variables: codes.map((code) => ({ code, value: Number(values[code] ?? '1') || 0 })),
        },
      });
      setOutcome(response);
    } catch (error) {
      console.error('Test de la formule impossible', error);
      setFailure('Le test a échoué, réessayer.');
    } finally {
      setIsRunning(false);
    }
  };

  if (!expression.trim()) return null;

  return (
    <div className={isOpen ? 'overflow-hidden rounded-lg border border-gray-200' : ''}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 ${
          isOpen ? 'w-full border-b border-gray-100 bg-gray-50 px-3 py-2' : 'px-1 py-0.5'
        }`}
      >
        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <FlaskConical className="h-3.5 w-3.5" />
        Tester la formule
        {isOpen && (
          <span className="ml-1 font-normal text-gray-400">
            {codes.length > 0
              ? 'Une valeur d’essai par variable, rien n’est enregistré.'
              : 'Aucune variable, le résultat est constant.'}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {codes.length > 0 && (
            <div className="grid gap-x-4 gap-y-3 px-3 py-3 [grid-template-columns:repeat(auto-fill,minmax(12rem,1fr))]">
              {codes.map((code) => {
                const variable = variableOf(code);
                return (
                  <label key={code} className="flex flex-col gap-1">
                    <span className="flex items-center gap-1.5 text-xs text-gray-600">
                      <span className="truncate" title={code}>
                        {variable?.libelle ?? code}
                      </span>
                      {variable?.unite && <span className="text-gray-400">({variable.unite})</span>}
                      {!variable && (
                        <span className="rounded bg-amber-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                          hors tunnel
                        </span>
                      )}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      disabled={disabled}
                      value={values[code] ?? '1'}
                      onChange={(event) => setValues({ ...values, [code]: event.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 font-mono text-sm focus:border-primary focus:outline-none"
                    />
                  </label>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-3 py-2.5">
            <Button color="primary" onClick={run} disabled={disabled || isRunning}>
              {isRunning ? 'Calcul…' : 'Calculer'}
            </Button>

            {failure && (
              <span className="flex items-center gap-1.5 text-xs text-red-600">
                <AlertCircle className="h-3.5 w-3.5" />
                {failure}
              </span>
            )}

            {outcome &&
              (outcome.isSuccess ? (
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Résultat
                  <span className="rounded-md bg-green-50 px-2 py-0.5 font-mono text-base font-semibold text-green-800">
                    {outcome.result}
                  </span>
                </span>
              ) : (
                <span className="flex items-start gap-1.5 text-xs text-red-600">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {outcome.errorMessage}
                </span>
              ))}
          </div>

          {outcome && outcome.canonicalExpression !== canonical && (
            <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
              Sera enregistrée sous la forme{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-700">
                {outcome.canonicalExpression}
              </code>
            </div>
          )}
        </>
      )}
    </div>
  );
};
